/* eslint-disable react-refresh/only-export-components */
/**
 * App Providers
 * Wraps the app with all required providers (QueryClient, Jotai, MSAL)
 *
 * IMPORTANT: Uses useHydrateAtoms to share QueryClient between
 * TanStack Query's QueryClientProvider and jotai-tanstack-query atoms.
 * Without this, queryClient.invalidateQueries() won't affect jotai atoms.
 * See: https://jotai.org/docs/extensions/query
 */

import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Provider as JotaiProvider } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { queryClientAtom } from "jotai-tanstack-query";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "@/config/msalConfig";

// Create a QueryClient instance with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default stale time of 1 minute
      staleTime: 60 * 1000,
      // Retry failed requests once
      retry: 1,
      // Disable refetch on window focus to reduce unnecessary requests
      // Data will still refresh on mutation invalidation and navigation
      refetchOnWindowFocus: false,
    },
  },
});

interface AppProvidersProps {
  children: React.ReactNode;
}

/**
 * HydrateAtoms component
 * Initializes jotai-tanstack-query's queryClientAtom with our shared QueryClient.
 * This ensures that queryClient.invalidateQueries() affects jotai atoms.
 */
function HydrateAtoms({ children }: { children: React.ReactNode }) {
  useHydrateAtoms([[queryClientAtom, queryClient]]);
  return <>{children}</>;
}

export function AppProviders({
  children,
}: AppProvidersProps): React.ReactElement {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <JotaiProvider>
          <HydrateAtoms>
            {children}
            {/* React Query Devtools - only in development */}
            {import.meta.env.DEV && (
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="top-right"
              />
            )}
          </HydrateAtoms>
        </JotaiProvider>
      </QueryClientProvider>
    </MsalProvider>
  );
}

// Export queryClient for use in tests or direct access
export { queryClient };
